import React, { useState, useRef, useEffect } from "react";
import ExpandedCaption from "./post_components/ExpandedCaption";

const TikTokCarousel = ({ downloaded_path, caption, creatorName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [inView, setInView] = useState(true); // Track if the image is in view
  const imageRef = useRef(null); // Reference for image element
  const startX = useRef(0);
  const startY = useRef(0);
  const isDragging = useRef(false);
  const hasMoved = useRef(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const node = imageRef.current;
    if (!node) return;

    const handleTouchMove = (e) => {
      moveDrag(e.touches[0].clientX, e.touches[0].clientY);
      e.preventDefault();
    };

    node.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      node.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  // Intersection Observer callback
  const handleIntersection = (entries) => {
    entries.forEach((entry) => {
      setInView(entry.isIntersecting); // Update state based on visibility
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.4, 
    });

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      if (imageRef.current) {
        observer.unobserve(imageRef.current);
      }
    };
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) =>
      prev < downloaded_path.length - 1 ? prev + 1 : 0
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prev) =>
      prev > 0 ? prev - 1 : downloaded_path.length - 1
    );
  };

  const startDrag = (x, y) => {
    isDragging.current = true;
    hasMoved.current = false;
    startX.current = x;
    startY.current = y;
  };

  const moveDrag = (x, y) => {
    if (!isDragging.current) return;
    const diffX = x - startX.current;
    const diffY = y - startY.current;

    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (!hasMoved.current) {
        hasMoved.current = true;
        diffX < 0 ? nextSlide() : prevSlide();
      }
    }
  };

  const endDrag = () => {
    isDragging.current = false;
    hasMoved.current = false;
  };

  const currentSrc = `https://genz-staging.feeltiptop.com/${downloaded_path[currentIndex]}`;

  const maxCaptionLength = 100;
  const truncatedCaption =
    caption && caption.length > maxCaptionLength
      ? caption.substring(0, maxCaptionLength) + "..."
      : caption;

  return (
    <div
      className="relative mx-auto w-[400px] h-auto overflow-hidden select-none"
      onTouchStart={(e) =>
        startDrag(e.touches[0].clientX, e.touches[0].clientY)
      }
      // onTouchMove={(e) => {
      //   moveDrag(e.touches[0].clientX, e.touches[0].clientY);
      //   e.preventDefault(); // Prevent scrolling during touch move
      // }}
      onTouchEnd={endDrag}
      onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
      onMouseMove={(e) => {
        if (isDragging.current) e.preventDefault(); // Prevent default scrolling during drag
        moveDrag(e.clientX, e.clientY);
      }}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
    >
      <div
        className={`absolute inset-0 transition-opacity duration-500 ${
          inView ? "opacity-0" : "opacity-60 bg-black"
        }`}
      ></div>
      <img
        ref={imageRef}
        className="w-full h-auto object-cover"
        src={currentSrc}
        alt={`Slide ${currentIndex + 1}`}
        loading="lazy"
        draggable={false}
      />

      {/* Caption and Creator Name Overlay, conditional */}
      {caption && (
                <div
                style={{
                  position: "absolute",
                  bottom: "2rem",
                  left: "1rem",
                  right: "1rem",
                  color: "white",
                  textShadow: "2px 2px 4px rgba(0, 0, 0, 0.7)",
                  fontSize: "12px",
                  zIndex: 30,
                }}
                >
                  {creatorName && (
                    <p
                      style={{ margin: 0, fontWeight: "bold", textAlign: "left" }}
                    >
                      @{creatorName}
                    </p>
                  )}
                  {caption && (
                    <div>
                      {!isExpanded && (
                        <p style={{ margin: 0, textAlign: "left" }}>
                          {truncatedCaption}
                          {caption.length > maxCaptionLength && (
                            <button
                              onClick={() => setIsExpanded(true)}
                              style={{
                                background: "transparent",
                                color: "#fff",
                                border: "none",
                                fontSize: "14px",
                                fontWeight: "bold",
                                cursor: "pointer",
                                textDecoration: "underline",
                                paddingLeft: "4px",
                              }}
                            >
                              more
                            </button>
                          )}
                        </p>
                      )}
                      {isExpanded && (
                        <ExpandedCaption
                          caption={caption}
                          onCollapse={() => setIsExpanded(false)}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
      <div
        style={{
          position: "absolute",
          bottom: "2rem",
          left: "1rem",
          right: "1rem",
          color: "white",
          textShadow: "2px 2px 4px rgba(0, 0, 0, 0.7)",
          fontSize: "12px",
          zIndex: 30,
        }}
      >
      </div>
      <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 transform space-x-1">
        {downloaded_path.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`h-2 w-2 rounded-full ${
              currentIndex === index ? "bg-white" : "bg-gray-500"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default TikTokCarousel;